/**
 * Which pixels an AI generation actually uploads.
 *
 * Every pipeline here — enhance, image→video, first/last-frame transition — funnels its source
 * through `resizeImageForUpload`, and its source is a shot's `thumbnailDataUrl`. In embedded mode
 * that field no longer holds the picture: the slim DB ships `staticimagedata` as X'', so an imported
 * still arrives as a `file://` URL pointing at Unity's REDUCED cache thumbnail (~480p, the right
 * pixel count for a tile and for nothing else). Uploading that would quietly turn "enhance my 12MP
 * still" into "enhance a 480p crop of it" — the one real quality regression in this wave, and the
 * reason `requestShotFullImage` grew an awaitable form.
 *
 * This module owns only the POLICY — when it is right to pay for the round trip. The verb itself,
 * its cache, its dedupe and its evictions all stay in `bridge/features/fullImage.ts`; nothing here
 * talks to Unity directly.
 */

import { capabilities } from '@/capabilities.ts';
import { requestFullImageAsync } from '@/bridge/features/fullImage.ts';
import type { TimelineShot } from '@/types/timeline.ts';

/**
 * Resolve the URL a generation should upload for `shot`, given the source the caller has already
 * picked (`candidateUrl` — usually `shot.thumbnailDataUrl`, but see the identity check below).
 *
 * Returns `candidateUrl` unchanged on every path that is not "an embedded static shot showing its
 * cache thumbnail", including the failure paths: a null answer (Unity has nothing, the entry was
 * invalidated mid-flight, or the request timed out) degrades the generation's INPUT RESOLUTION and
 * nothing else. This function never throws and never blocks longer than the wrapper's timeout, so no
 * generation can be lost to a bridge that went quiet.
 *
 * The four gates, in the order they get cheaper to be wrong about:
 *
 * 1. `capabilities.bridge` — belt-and-braces. Standalone and online-review hold real `data:`/`blob:`
 *    pixels and gate 4 would already cover them, but a request sent where no Unity is listening
 *    costs the full timeout, so it is worth one boolean to make those modes provably untouched.
 * 2. `hasStaticImage` — a RENDERED shot's cache thumbnail is the only image that exists for it;
 *    there is no fuller version to ask for.
 * 3. `candidateUrl === shot.thumbnailDataUrl` — the discriminator for history-based inputs, which
 *    this wave deliberately leaves alone. Re-enhance rolls back to the ORIGINAL image recorded in
 *    generation history (`aiOrchestrator`'s `getInputImageUrl` branch); that URL belongs to a history
 *    asset, not to this shot's static blob, and Unity's `{shotId}_full` extraction would answer with
 *    the wrong picture entirely. Identity, not scheme, is what separates them — a history asset can
 *    be a `file://` URL too.
 *
 *    CAVEAT: this gate only discriminates for REAL store shots. A caller that synthesizes a shot by
 *    spreading and overriding `thumbnailDataUrl` (batch touch-up's `effectiveShot`) satisfies the
 *    identity check BY CONSTRUCTION — the spread makes the two strings equal again — so such a
 *    caller must also set `hasStaticImage: false` in the override, or gate 2 is its only real
 *    protection and only until the override's URL scheme changes. Both existing synthetic callers
 *    do this; any new one must too.
 * 4. not `data:`/`blob:` — those ARE the full pixels, already in the JS heap: a standalone load, a
 *    just-dropped file before Unity's push lands, or a generated image the user is touching up.
 */
export async function resolveGenerationInputImage(
  shot: TimelineShot,
  candidateUrl: string,
): Promise<string> {
  if (!capabilities.bridge) return candidateUrl;
  if (!shot.hasStaticImage) return candidateUrl;
  if (candidateUrl !== shot.thumbnailDataUrl) return candidateUrl;
  if (candidateUrl.startsWith('data:') || candidateUrl.startsWith('blob:')) return candidateUrl;

  return (await requestFullImageAsync(shot.id)) ?? candidateUrl;
}
